import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import Container from '../layout/Container';
import SectionTitle from '../ui/SectionTitle';
import PortfolioCard from '../cards/PortfolioCard';
import { resolveLinkedProject } from '../../utils/portfolioMatching';
import { publicApi } from '../../services/publicApi';

const Portfolio = () => {
  const [portfolio, setPortfolio] = useState([]);
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    let isMounted = true;
    const loadPortfolioCards = async () => {
      try {
        const [overviewData, projectData] = await Promise.all([
          publicApi.getPortfolioOverviews(),
          publicApi.getProjects(),
        ]);
        if (!isMounted) return;

        if (!Array.isArray(overviewData) || overviewData.length === 0) {
          setPortfolio([]);
          setProjects([]);
          return;
        }

        const activeProjects = Array.isArray(projectData)
          ? projectData.filter((item) => item.isActive !== false)
          : [];

        const mapped = overviewData
          .filter((item) => item.isActive !== false)
          .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0))
          .map((item) => {
            const linkedProject = resolveLinkedProject(item, activeProjects);
            return {
              id: item.id,
              projectId: item.projectId || linkedProject?.id,
              slug: item.slug || linkedProject?.slug || String(item.id),
              title: item.title || linkedProject?.title || '',
              category: item.category || linkedProject?.category || 'Project',
              description:
                item.shortDescription ||
                linkedProject?.description ||
                linkedProject?.longDescription ||
                '',
              image: item.mainImageUrl || linkedProject?.mainImageUrl || '',
              year: String(item.year || linkedProject?.year || ''),
              industry: item.industry || linkedProject?.industry || item.category || '',
              direction: item.projectDirection || linkedProject?.projectDirection || '',
            };
          });
        setProjects(activeProjects);
        setPortfolio(mapped);
      } catch {
        if (isMounted) {
          setPortfolio([]);
          setProjects([]);
        }
      }
    };

    loadPortfolioCards();
    return () => {
      isMounted = false;
    };
  }, []);

  if (portfolio.length === 0) return null;

  return (
    <Container id="portfolio" className="transition-colors duration-300">
      <SectionTitle
        title="Our Recent Work"
        subtitle="Portfolio"
      />

      <motion.p
        initial={{ opacity: 0, y: 18 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.45 }}
        className="text-gray-500 dark:text-gray-300 text-center max-w-2xl mx-auto -mt-6 mb-10"
      >
        Explore selected projects built by RaadsoTech across web apps, mobile products, and business platforms.
      </motion.p>

      <div className="mt-6 grid grid-cols-1 gap-8 md:grid-cols-2 md:gap-7 lg:grid-cols-3">
        {portfolio.map((item, index) => (
          <PortfolioCard
            key={`${item.id}-${item.image || ''}-${item.title || ''}`}
            item={item}
            index={index}
            projects={projects}
          />
        ))}
      </div>
    </Container>
  );
};

export default Portfolio;
